import type { AxiosError } from 'axios'
import { Navigate, useParams } from 'react-router-dom'

import FullPageSpinner from '@/components/feedback/FullPageSpinner'
import { ControllerRouteInformation } from '@/features/controller/constants'
import { useControllerByEquipmentNumberQuery } from '@/features/controller/hooks/useControllerByEquipmentNumberQuery'
import { UnregisteredControllerPage } from '@/features/controller/pages/UnregisteredControllerPage'
import NavigateToError from '@/features/error/pages/NavigateToError'

function parseEquipmentNumber(value: string | undefined): number | null {
  return value !== undefined && /^\d+$/.test(value) ? Number(value) : null
}

export function ControllerByEquipmentNumberPage() {
  const { controllerEquipmentNumber } = useParams()
  const equipmentNumber = parseEquipmentNumber(controllerEquipmentNumber)
  const { controllerDescription, error, isLoading, isSuccess } = useControllerByEquipmentNumberQuery(
    equipmentNumber ?? 0,
    {
      enabled: equipmentNumber !== null,
    },
  )

  if (equipmentNumber === null) {
    return <NavigateToError statusCode={404} />
  }

  if (isLoading) {
    return <FullPageSpinner />
  }

  if (controllerDescription) {
    return (
      <Navigate replace to={ControllerRouteInformation.CONTROLLER_DETAILS.navigationPath(controllerDescription.id)} />
    )
  }

  if (isSuccess) {
    // No controller from a successful lookup means Device Manager answered 404.
    return <UnregisteredControllerPage equipmentNumber={equipmentNumber} />
  }

  // The global interceptor also navigates on a 403, but this render would override it.
  return <NavigateToError statusCode={(error as AxiosError | null)?.status === 403 ? 403 : 500} />
}
